import React from 'react';
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Stack,
  Chip,
  IconButton,
  Button,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { Eye, UserPlus, Star, Info, ChevronLeft, ChevronRight } from 'lucide-react';
import { NameEmailAvatar } from '../../../../ui/components/Common/NameEmailAvatar';
import {
  BankSearch,
  BankSearchPool,
  BankSearchResult,
} from '../../../../utils/types/candi_bank_types';
import { educationSummary } from '../../utils/bankSearchAdapters';

interface BankSearchResultsTableProps {
  results: BankSearchResult[];
  pool: BankSearchPool | null;
  search: BankSearch;
  total: number;
  page: number;
  limit: number;
  loading: boolean;
  onPageChange: (page: number) => void;
  onViewProfile: (result: BankSearchResult) => void;
  onInvite: (result: BankSearchResult) => void;
}

export const BankSearchResultsTable: React.FC<BankSearchResultsTableProps> = ({
  results,
  pool,
  search,
  total,
  page,
  limit,
  loading,
  onPageChange,
  onViewProfile,
  onInvite,
}) => {
  const totalPages = Math.ceil(total / limit);
  const startItem = total > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, total);

  const getPageNumbers = () => {
    const pages = [];
    for (let i = Math.max(1, page - 2); i <= Math.min(totalPages, page + 2); i++) {
      pages.push(i);
    }
    return pages;
  };

  const renderSkillChips = (result: BankSearchResult) => {
    interface ChipData {
      label: string;
      type: 'required' | 'mandatory' | 'desirable';
      status: 'matched' | 'missing';
      evidence?: string;
    }
    const chips: ChipData[] = [];

    // Required
    search.requiredSkills?.forEach((skill) => {
      const isMatched = result.matchedRequired?.includes(skill);
      chips.push({
        label: skill,
        type: 'required',
        status: isMatched ? 'matched' : 'missing',
        evidence: result.matchEvidence?.[skill],
      });
    });

    // Mandatory
    search.mandatorySkills?.forEach((skill) => {
      const isMatched = result.matchedMandatory?.includes(skill);
      chips.push({
        label: skill,
        type: 'mandatory',
        status: isMatched ? 'matched' : 'missing',
        evidence: result.matchEvidence?.[skill],
      });
    });

    // Desirable (only matched)
    result.matchedDesirable?.forEach((skill) => {
      chips.push({
        label: skill,
        type: 'desirable',
        status: 'matched',
        evidence: result.matchEvidence?.[skill],
      });
    });

    const displayChips = chips.slice(0, 6);
    const extraCount = chips.length - 6;

    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
        {displayChips.map((chip, idx) => {
          let chipColor: 'default' | 'success' | 'error' | 'info' = 'default';
          let chipVariant: 'outlined' | 'filled' = 'outlined';

          if (chip.type === 'required') {
            chipColor = chip.status === 'matched' ? 'success' : 'error';
            chipVariant = 'outlined';
          } else if (chip.type === 'mandatory') {
            chipColor = chip.status === 'matched' ? 'success' : 'error';
            chipVariant = 'filled';
          } else if (chip.type === 'desirable') {
            chipColor = 'info';
            chipVariant = 'outlined';
          }

          const title =
            chip.status === 'matched' && chip.evidence
              ? `matched "${chip.evidence}"`
              : undefined;

          return (
            <Chip
              key={idx}
              label={chip.label}
              size="small"
              color={chipColor}
              variant={chipVariant}
              title={title}
              sx={{
                fontSize: '0.6875rem',
                height: 22,
                fontWeight: 500,
              }}
            />
          );
        })}
        {extraCount > 0 && (
          <Chip
            label={`+${extraCount} more`}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.625rem', height: 20, color: 'text.secondary' }}
          />
        )}
      </Stack>
    );
  };

  const renderScoreBar = (result: BankSearchResult) => {
    const scoreVal = result.score || 0;
    const percent = Math.min(100, Math.max(0, scoreVal * 100));
    const breakdownEntries = Object.entries(result.breakdown || {});
    const title =
      breakdownEntries.length > 0
        ? breakdownEntries
            .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${(v as number).toFixed(2)}`)
            .join(' · ')
        : `Score: ${scoreVal.toFixed(2)}`;

    return (
      <Box sx={{ width: 110, cursor: 'help' }} title={title}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 700, fontFamily: 'monospace' }}>
            {scoreVal.toFixed(2)}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.625rem' }}>
            {Math.round(percent)}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{
            height: 6,
            borderRadius: 3,
            bgcolor: 'grey.100',
            '& .MuiLinearProgress-bar': {
              borderRadius: 3,
            },
          }}
        />
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        border: '1px solid',
        borderColor: 'grey.200',
        borderRadius: 3,
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box sx={{ position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255, 255, 255, 0.65)',
              backdropFilter: 'blur(2px)',
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}

        <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50' }}>
                  Candidate
                </TableCell>
                <TableCell sx={{ width: 140, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50' }}>
                  Match Score
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50' }}>
                  Skills & Evidence
                </TableCell>
                <TableCell align="right" sx={{ width: 110, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', bgcolor: 'grey.50' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {results.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 8 }}>
                    {pool && pool.ranked === 0 ? (
                      <Stack spacing={1} sx={{ maxWidth: 440, mx: 'auto' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          No candidates passed the criteria
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                          All {pool.people || 0} candidates are under the experience bar ({search.minimumWorkExperience} yrs) or missing hard requirements. Try lowering the experience threshold or moving skills to &ldquo;Important&rdquo;.
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        No results found for this search.
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              )}

              {results.map((result, i) => {
                const fullName = `${result.firstName || ''} ${result.lastName || ''}`.trim() || 'Unknown Candidate';
                const education = educationSummary(result.educations);

                return (
                  <TableRow
                    key={result.id || i}
                    hover
                    onClick={() => onViewProfile(result)}
                    sx={{ cursor: 'pointer' }}
                  >
                    {/* Candidate Profile Info */}
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <NameEmailAvatar
                          name={fullName}
                          email={result.email || ''}
                          avatar=""
                          avatarWidth="34px"
                          avatarHeight="34px"
                          nameFontSize="13px"
                          emailFontSize="11px"
                        />
                        <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                          <Box component="span" title="AI Profile Match" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                            <Star size={13} color="#9333ea" />
                          </Box>

                          {result.alreadyApplied && (
                            <Chip
                              label="Applied"
                              size="small"
                              color="primary"
                              sx={{ height: 18, fontSize: '0.5625rem', fontWeight: 700, textTransform: 'uppercase' }}
                            />
                          )}

                          {result.years !== undefined && (
                            <Chip
                              label={`${result.years}y ${!result.datesVerified ? '(unverified)' : ''}`}
                              size="small"
                              variant="outlined"
                              color={result.datesVerified ? 'success' : 'default'}
                              sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }}
                            />
                          )}

                          {education && (
                            <Chip
                              label={education}
                              size="small"
                              title={education}
                              sx={{ height: 18, fontSize: '0.625rem', maxWidth: 200 }}
                            />
                          )}

                          {result.jobSeekingStatus && (
                            <Chip
                              label={result.jobSeekingStatus}
                              size="small"
                              sx={{ height: 18, fontSize: '0.625rem' }}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>

                    {/* Match Score */}
                    <TableCell>
                      {renderScoreBar(result)}
                    </TableCell>

                    {/* Skill Badges */}
                    <TableCell>
                      {renderSkillChips(result)}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <IconButton
                          size="small"
                          onClick={() => onViewProfile(result)}
                          title="View Profile"
                          sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                          <Eye size={16} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => onInvite(result)}
                          title="Add to Opening / Invite List"
                          sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                        >
                          <UserPlus size={16} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Footer: Funnel stats & Pagination */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'grey.100',
          bgcolor: 'grey.50',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        {/* Funnel Line */}
        {pool && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Info size={14} color="#64748b" />
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              <strong>{pool.ranked || 0}</strong> of <strong>{pool.people || 0}</strong> people ranked · <strong>{pool.gatedByExperience || 0}</strong> under experience bar · <strong>{pool.unverified || 0}</strong> unverified dates
            </Typography>
          </Stack>
        )}

        {/* Pagination controls */}
        {total > 0 && (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ ml: 'auto' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Showing <strong>{startItem}-{endItem}</strong> of <strong>{total}</strong>
            </Typography>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              <IconButton
                size="small"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
              >
                <ChevronLeft size={16} />
              </IconButton>

              {getPageNumbers().map((p) => (
                <Button
                  key={p}
                  size="small"
                  variant={p === page ? 'contained' : 'text'}
                  onClick={() => onPageChange(p)}
                  sx={{
                    minWidth: 28,
                    height: 28,
                    p: 0,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}
                >
                  {p}
                </Button>
              ))}

              <IconButton
                size="small"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
              >
                <ChevronRight size={16} />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Box>
    </Paper>
  );
};
